
import { Provider } from 'react-redux';
import { RouterProvider } from 'react-router';
import { useEffect, useState } from 'react';
import { store } from './redux/store';
import { router } from './routes/router';
import { fetchRestaurantsRequest } from './redux/slices/restaurantSlice';
import { useAppDispatch, useAppSelector } from './redux/hooks';
import Loader from './components/Loader';
import WebsiteUnavailable from './components/WebsiteUnavailable';
import { TitleUpdater } from './components';
import { AIImageGenerationDrawer } from './components/AIImageGenerationDrawer';
import { AITextGenerationDrawer } from './components/AITextGenerationDrawer';
import { useGlobalAIDrawers } from './hooks/useGlobalAIDrawers';
import { shouldSkipApiCalls } from './utils/appConfig';
import { useAppStyle } from './hooks/useAppStyle';

function AppContent() {
  const dispatch = useAppDispatch();
  
  // Initialize app-wide styling (replaces StyleProvider)
  useAppStyle();
  const { loading: restaurantLoading, selectedRestaurant } = useAppSelector(state => state.restaurant);
  const { fetchLoading: siteContentLoading, data: siteContent } = useAppSelector(
    state => state.siteContent
  );
  const { hasCommerceAI } = useAppSelector(state => state.subscription);

  const [appLoading, setAppLoading] = useState(false);
  const {
    imageDrawer,
    textDrawer,
    closeImageDrawer,
    closeTextDrawer
  } = useGlobalAIDrawers();

  useEffect(() => {
    dispatch(fetchRestaurantsRequest());
  }, [dispatch]);

  useEffect(() => {
    // If in preview mode, don't show loader for API-related loading states
    if (shouldSkipApiCalls()) {
      setAppLoading(false);
    } else {
      setAppLoading(siteContentLoading || restaurantLoading);
    }
  }, [siteContentLoading, restaurantLoading, siteContent]);

  // Check subscription status - show unavailable page if commerce_ai addon is not active
  if (!appLoading && !hasCommerceAI && selectedRestaurant) {
    return <WebsiteUnavailable phone={selectedRestaurant.phone} />;
  }

  return (
    <>
      <TitleUpdater />
      <RouterProvider router={router} />
      <Loader
        message="Getting everything ready for you..."
        loading={appLoading}
      />

      {/* Global AI Drawers - Best Practices Pattern */}
      <AIImageGenerationDrawer
        isOpen={!!imageDrawer}
        onClose={closeImageDrawer}
        onImageSelect={imageDrawer?.onImageSelect || (() => { })}
        defaultPrompt={imageDrawer?.prompt}
      />

      <AITextGenerationDrawer
        isOpen={!!textDrawer}
        onClose={closeTextDrawer}
        onTextSelect={textDrawer?.onTextSelect || (() => { })}
        defaultPrompt={textDrawer?.prompt}
        currentFieldContent={textDrawer?.currentContent}
      />
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <AppContent />
    </Provider>
  );
}

export default App;
